import { memo, useCallback, MouseEvent } from 'react';

import {
  FiPlay,
  FiPause,
  FiVolume2,
  FiVolumeX,
  FiVolume1,
  FiMaximize,
  FiMinimize,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

import * as S from '../styles';

type ControlsProps = {
  isPlaying: boolean;
  isMuted: boolean;
  volume: number;
  played: number;
  loaded: number;
  duration: number;
  currentTime: number;
  isFullscreen: boolean;
  isVisible: boolean;
  isLive?: boolean;
  isAtLiveEdge?: boolean;
  onSeekToLive?: () => void;
  onTogglePlay: () => void;
  onToggleMute: () => void;
  onVolumeChange: (volume: number) => void;
  onSeekStart: () => void;
  onSeek: (value: number) => void;
  onSeekEnd: (value: number) => void;
  onToggleFullscreen: () => void;
  formatTime: (seconds: number) => string;
  extraControls?: React.ReactNode;
};

const Controls = ({
  isPlaying,
  isMuted,
  volume,
  played,
  loaded,
  duration,
  currentTime,
  isFullscreen,
  isVisible,
  isLive = false,
  isAtLiveEdge = true,
  onSeekToLive,
  onTogglePlay,
  onToggleMute,
  onVolumeChange,
  onSeekStart,
  onSeek,
  onSeekEnd,
  onToggleFullscreen,
  formatTime,
  extraControls,
}: ControlsProps) => {
  const { t } = useTranslation();

  const handleProgressClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      const clampedPos = Math.max(0, Math.min(1, pos));
      onSeekEnd(clampedPos);
    },
    [onSeekEnd],
  );

  const handleProgressMouseDown = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      onSeekStart();
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      onSeek(Math.max(0, Math.min(1, pos)));
    },
    [onSeekStart, onSeek],
  );

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onVolumeChange(parseFloat(e.target.value));
    },
    [onVolumeChange],
  );

  const getVolumeIcon = () => {
    if (isMuted || volume === 0) return FiVolumeX;
    if (volume < 0.5) return FiVolume1;
    return FiVolume2;
  };

  const VolumeIcon = getVolumeIcon();

  return (
    <S.ControlsWrapper $visible={isVisible}>
      {/* Progress Bar */}
      {!isLive && (
        <S.ProgressContainer
          onClick={handleProgressClick}
          onMouseDown={handleProgressMouseDown}
        >
          <S.ProgressBar>
            <S.ProgressLoaded $progress={loaded} />
            <S.ProgressPlayed $progress={played} />
          </S.ProgressBar>
        </S.ProgressContainer>
      )}

      {/* Controls Row */}
      <S.ControlsRow>
        <S.ControlsLeft>
          {/* Play/Pause */}
          <S.ControlButton onClick={onTogglePlay} type="button">
            {isPlaying ? <FiPause /> : <FiPlay />}
          </S.ControlButton>

          {/* Volume */}
          <S.VolumeContainer>
            <S.ControlButton onClick={onToggleMute} type="button">
              <VolumeIcon />
            </S.ControlButton>
            <S.VolumeSliderWrapper>
              <S.VolumeSlider
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
              />
            </S.VolumeSliderWrapper>
          </S.VolumeContainer>

          {/* Time Display */}
          {!isLive && (
            <S.TimeDisplay>
              {formatTime(currentTime)} / {formatTime(duration)}
            </S.TimeDisplay>
          )}

          {/* Live Badge */}
          {isLive && (
            <S.LiveBadge
              $active={isAtLiveEdge}
              onClick={onSeekToLive}
              type="button"
            >
              {t('rooms.live', 'Ao Vivo')}
            </S.LiveBadge>
          )}
        </S.ControlsLeft>

        <S.ControlsRight>
          {extraControls}
          {/* Fullscreen */}
          <S.ControlButton onClick={onToggleFullscreen} type="button">
            {isFullscreen ? <FiMinimize /> : <FiMaximize />}
          </S.ControlButton>
        </S.ControlsRight>
      </S.ControlsRow>
    </S.ControlsWrapper>
  );
};

export default memo(Controls);
